import React from 'react';
import { PortalName } from '@/common/DataModel/PortalData';
import { GehaLogo, GehaV2Logo } from '@/components/Logo';

export interface PortalThemeConfig {
  primaryColor?: string;
  secondaryColor?: string;
  tertiaryColor?: string;
  quaternaryColor?: string;
  logo?: JSX.Element;
  fontOverride?: object;
}

export interface PortalThemeWithVersions {
  v1: PortalThemeConfig;
  v2?: PortalThemeConfig;
}

type PortalNameTheme = {
  [key in PortalName]: PortalThemeWithVersions;
};

export const portalNameThemeMap: PortalNameTheme = {
  [PortalName.UMR]: {
    v1: {},
  },
  [PortalName.GEHA]: {
    v1: {
      primaryColor: '#1C0950',
      secondaryColor: '#500778',
      tertiaryColor: '#E8E9F3',
      quaternaryColor: '#500778',
      logo: <GehaLogo />,
    },
    v2: {
      primaryColor: '#00172F',
      secondaryColor: '#4C728A',
      tertiaryColor: '#CAD9E2',
      quaternaryColor: '#4C728A',
      logo: <GehaV2Logo />,
      fontOverride: {
        primary: 'Arial, sans-serif',
        heading: 'Arial, AbyssUHCSerif, Georgia Bold, serif',
        'web-core-fontFamily-enterpriseSansVf': 'Arial',
        'web-core-fontFamily-uhc2020Sans': 'Arial',
        'web-core-fontFamily-uhcSerifHeadline': 'Arial',
      },
    },
  },
  [PortalName.STUDENT_RESOURCE]: {
    v1: {},
  },
  [PortalName.ALL_SAVERS]: {
    v1: {},
  },
  [PortalName.UHONE]: {
    v1: {},
  },
  [PortalName.FLEX_WORK]: {
    v1: {},
  },
};

export const createThemeOverride = ({
  primaryColor,
  secondaryColor,
  tertiaryColor,
  quaternaryColor,
  fontOverride,
}: PortalThemeConfig): object => {
  const primary1 = primaryColor ?? '#002677';
  const interactive1 = secondaryColor ?? '#196ECF';
  const interactive3 = tertiaryColor ?? '#D9E9FA';
  const interactive2 = quaternaryColor ?? '#004BA0';

  return {
    theme: {
      colors: {
        // Overrides for tokens that use primary1 color value
        primary1,
        footerBackground: '$primary1',
        carouselContentPrimary: '$primary1',
        carouselButtonDefault: '$primary1',
        loadingSpinnerPrimary1: '$primary1',
        avatarContentAquaBorderColor: '$primary1',
        avatarContentBlueBorderColor: '$primary1',
        navMenuBackgroundDefault: '$primary1',
        navMenuTextHover: '$primary1',
        'core-color-brand-100': '$primary1',
        'semantic-color-surface-interactive-standards-rest-default-primary':
          '$primary1',
        'semantic-color-surface-interactive-standards-active-selected-tertiary':
          '$primary1',
        'semantic-color-surface-interactive-controls-rest-selected-pickers':
          '$primary1',
        'semantic-color-surface-interactive-controls-active-selected-choice':
          '$primary1',
        'semantic-color-surface-interactive-headerElement-rest-default':
          '$primary1',
        'semantic-color-surface-container-primary': '$primary1',
        'semantic-color-surface-container-header-topLevelNav': '$primary1',
        'semantic-color-border-interactive-controls-active-selected':
          '$primary1',
        'semantic-color-border-content-primary': '$primary1',
        'semantic-color-icon-interactive-rest-primary': '$primary1',
        'semantic-color-icon-interactive-active-tertiary': '$primary1',
        'semantic-color-icon-content-primary': '$primary1',
        'semantic-color-text-interactive-active-primary': '$primary1',
        'semantic-color-text-label-cta-primary': '$primary1',
        'semantic-color-text-content-primary': '$primary1',
        'accordionV2-color-text-trigger': '$primary1',
        'accordionV2-color-fill-chevron-default': '$primary1',
        'accordionV2-color-fill-headerIcon': '$primary1',
        'breadcrumbsV2-color-text-link-active': '$primary1',
        'button-color-text-root-tertiary-default': '$primary1',
        'buttonV2-color-text-root-secondary': '$primary1',
        'buttonV2-color-text-root-tertiary-active': '$primary1',
        'buttonV2-color-background-root-primary-default': '$primary1',
        'buttonV2-color-border-root-secondary': '$primary1',
        'buttonV2-color-fill-svg-secondary-default': '$primary1',
        'buttonV2-color-fill-svg-tertiary-active': '$primary1',
        'footerV2-color-background-root-default': '$primary1',
        'formInputV2-color-background-root-inputType-checkbox-checked-active':
          '$primary1',
        'linkV2-color-text-active': '$primary1',
        'navMenu-color-background-link-inverted-hover': '$primary1',
        'navMenu-color-background-menuTrigger-inverted-hover': '$primary1',
        'navMenu-color-background-root-default': '$primary1',
        'navMenu-color-background-scrollButton-default-default': '$primary1',
        'navMenu-color-background-scrollButton-inverted-hover': '$primary1',
        'navMenu-color-fill-columnTitleExternalIcon': '$primary1',
        'navMenu-color-fill-menuTriggerIcon-default-open-default': '$primary1',
        'navMenu-color-fill-menuTriggerIcon-default-open-hover': '$primary1',
        'navMenu-color-fill-menuTriggerIcon-inverted-closed': '$primary1',
        'navMenu-color-fill-scrollButtonIcon-default-default': '$primary1',
        'navMenu-color-fill-scrollButtonIcon-inverted-default': '$primary1',
        'navMenu-color-text-columnTitle-default': '$primary1',
        'navMenu-color-text-link-inverted-default': '$primary1',
        'navMenu-color-text-menuTrigger-default-hover': '$primary1',
        'navMenu-color-text-menuTrigger-inverted-default': '$primary1',
        'navMenuV2-color-background-default-default': '$primary1',
        'navMenuV2-color-background-link-inverted-hover': '$primary1',
        'navMenuV2-color-background-menuTrigger-inverted-hover': '$primary1',
        'navMenuV2-color-background-root-default': '$primary1',
        'navMenuV2-color-background-scrollButton-default-default': '$primary1',
        'navMenuV2-color-background-scrollButton-inverted-hover': '$primary1',
        'navMenuV2-color-fill-columnTitleExternalIcon': '$primary1',
        'navMenuV2-color-fill-menuTriggerIcon-default-open-default':
          '$primary1',
        'navMenuV2-color-fill-menuTriggerIcon-default-open-hover': '$primary1',
        'navMenuV2-color-fill-menuTriggerIcon-inverted-closed': '$primary1',
        'navMenuV2-color-fill-scrollButtonIcon-default-default': '$primary1',
        'navMenuV2-color-fill-scrollButtonIcon-inverted-default': '$primary1',
        'navMenuV2-color-text-columnTitle-default': '$primary1',
        'navMenuV2-color-text-link-default-hover': '$primary1',
        'navMenuV2-color-text-link-inverted-default': '$primary1',
        'navMenuV2-color-text-menuTrigger-default-hover': '$primary1',
        'navMenuV2-color-text-menuTrigger-inverted-default': '$primary1',
        'pageBodyIntro-color-text-titleText': '$primary1',
        'pageFooter-color-background-root': '$primary1',
        // Overrides for tokens that use interactive1 color value
        interactive1,
        info1: '$interactive1',
        accent1: '$interactive1',
        toggleTabsSelected: '$interactive1',
        carouselContentSecondary: '$interactive1',
        'core-color-brand-60': '$interactive1',
        'core-color-blue-80': '$interactive1',
        'semantic-color-surface-interactive-standards-rest-selected-tertiary':
          '$interactive1',
        'semantic-color-surface-interactive-controls-rest-selected-choice':
          '$interactive1',
        'semantic-color-surface-interactive-headerElement-rest-selected':
          '$interactive1',
        'semantic-color-surface-container-status-info-saturated':
          '$interactive1',
        'semantic-color-border-interactive-controls-rest-selected':
          '$interactive1',
        'semantic-color-border-interactive-stateAffordance-selected':
          '$interactive1',
        'semantic-color-border-interactive-forms-hover': '$interactive1',
        'semantic-color-border-status-info': '$interactive1',
        'semantic-color-icon-interactive-rest-tertiary': '$interactive1',
        'semantic-color-icon-status-info': '$interactive1',
        'semantic-color-text-interactive-rest-primary': '$interactive1',
        'semantic-color-text-status-info': '$interactive1',
        'alert-color-border-actionButton-info': '$interactive1',
        'badge-color-text-container-info': '$interactive1',
        'badge-color-border-container-info': '$interactive1',
        'badgeV2-color-text-container-info': '$interactive1',
        'badgeV2-color-border-container-info': '$interactive1',
        'breadcrumbsV2-color-text-link-default': '$interactive1',
        'breadcrumbsV2-color-icon-mobile': '$interactive1',
        'buttonV2-color-text-root-tertiary-default': '$interactive1',
        'buttonV2-color-fill-svg-tertiary-default': '$interactive1',
        'checkboxV2-color-border-checked-true-rest': '$interactive1',
        'formInputV2-color-background-root-inputType-checkbox-checked-rest':
          '$interactive1',
        'formInputV2-color-border-root-default-hover': '$interactive1',
        'formInputV2-color-text-root-caret-default': '$interactive1',
        'formInputLabelV2-color-text-helperIcon': '$interactive1',
        'link-color-text-default': '$interactive1',
        'linkV2-color-text-default': '$interactive1',
        'navMenu-color-fill-itemLinkExternalIcon-default': '$interactive1',
        'navMenu-color-fill-routerHeaderIcon': '$interactive1',
        'navMenu-color-focus-root': '$interactive1',
        'navMenu-color-focus-drawerMenuItem': '$interactive1',
        'navMenu-color-text-drawerMenuItem': '$interactive1',
        'navMenu-color-text-itemLinkTitle-default': '$interactive1',
        'navMenuV2-color-fill-itemLinkExternalIcon-default': '$interactive1',
        'navMenuV2-color-fill-routerHeaderIcon': '$interactive1',
        'navMenuV2-color-focus-root': '$interactive1',
        'navMenuV2-color-focus-drawerMenuItem': '$interactive1',
        'navMenuV2-color-text-drawerMenuItem': '$interactive1',
        'navMenuV2-color-text-itemLinkTitle-default': '$interactive1',
        'pageBodyIntro-color-border-link-active': '$interactive1',
        'pageBodyIntro-color-text-profileHeadingText': '$interactive1',
        'selectInput-color-border-input-open': '$interactive1',
        'selectInput-color-border-optionListContainer-open': '$interactive1',
        'selectInput-color-fill-icon-default': '$interactive1',
        'selectInput-color-fill-icon-hover': '$interactive1',
        'stepIndicator-color-background-stepBar-active': '$interactive1',
        'stepIndicator-color-fill-scrollButton': '$interactive1',
        'stepIndicator-color-outline-stepIcon': '$interactive1',
        'tabs-color-border-activeLine': '$interactive1',
        'tabs-color-focusRing-tab': '$interactive1',
        'tabs-color-text-tab-default': '$interactive1',
        'toast-color-background-container-info': '$interactive1',
        // Overrides for tokens that use interactive2 color value
        interactive2,
        // Overrides for tokens that use interactive3 color value
        interactive3,
      },
      ...(fontOverride && { fonts: fontOverride }),
    },
  };
};
